import React, { useCallback, useEffect, useState, useRef, useMemo, ReactNode } from 'react';

import { useDatadogLog } from '../../logging';
import { extractTextFromJSX } from '../../../../utils';
import OutsideClickHandler from '../../../../vendor/OutsideClickHandler';
import Box from '../../../Layout/Box/Box';
import ErrorText from '../../../Text/ErrorText/ErrorText';
import Icon from '../../../DataDisplay/Icon/Icon';
import styles from './Select1.module.scss';

// CAREFUL WHEN REMOVING/ADDING PROPERTIES: ALSO USED IN FilterableMultiSelector
export type SelectOption<Data extends object = Record<string, never>> = {
  label: string;
  value: string;
  style?: React.CSSProperties;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  isLocked?: boolean;
} & Data;

export type CustomAppendNoResultErrorMessage = {
  label: string;
  style?: React.CSSProperties;
};

export type Select1Props<Data extends object = Record<string, never>> = {
  variant:
    | 'outlined'
    | 'standard'
    | 'plain'
    | 'chart'
    | 'background'
    | 'dropdown'
    | 'lined'
    | 'basic'
    | 'calendar-header'
    | 'none';
  options: Record<string, ReactNode> | SelectOption<Data>[];
  position?: 'absolute' | 'fixed';
  type?: 'select' | 'selectSearch' | 'selectCompany';
  icon?: React.JSX.Element;
  lockedValues?: Record<string, string>;
  reverse?: boolean;
  allowEmpty?: boolean;
  noOrdering?: boolean;
  orderByValues?: boolean;
  placeholder: string;
  name: string;
  value: string;
  labelOverride?: string;
  error?: string | string[] | null;
  disabled?: boolean;
  showValue?: boolean;
  labelColor?: string;
  placeholderColor?: string;
  allowNew?: boolean;
  selectionAreaHeight?: string;
  onFieldBlur?: boolean;
  isActive?: boolean;
  isLoading?: boolean;
  hasTwoValues?: boolean;
  shouldClearCurrentLabel?: boolean;
  borderColor?: string;
  disableFiltering?: boolean;
  id?: string;
  newTitle?: string;
  addNewStyle?: 'standard' | 'bolded';
  customAppendNoResultErrorMessage?: CustomAppendNoResultErrorMessage;
  onBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  lockedCallback?: (value: string) => void;
  setKeyword?: (value: string) => void;
  searchCallback?: (value: string) => void;
  enablePagination?: boolean;
  isReadOnly?: boolean;
  renderDropdownHeader?: ({ close }: { close: () => void }) => React.ReactNode;
  renderOption?: (o: SelectOption<Data>, args: { focused: boolean }) => React.ReactNode;
  renderSelectedValue?: (o: SelectOption<Data>) => React.ReactNode;
  optionVisibility?: (o: SelectOption<Data>) => boolean;
};

function Select1<Data extends object = Record<string, never>>({
  variant = 'outlined',
  type = 'select',
  options: optionsProp,
  lockedValues,
  reverse = false,
  noOrdering = false,
  orderByValues = false,
  placeholder,
  allowEmpty = false,
  name,
  value: input = '',
  error = null,
  disabled = false,
  showValue = false,
  labelColor,
  placeholderColor,
  allowNew = false,
  selectionAreaHeight = '300px',
  onFieldBlur = false,
  isActive,
  isLoading = false,
  hasTwoValues: hasTwoValueProp = false,
  shouldClearCurrentLabel,
  icon,
  borderColor,
  labelOverride,
  disableFiltering = false,
  id,
  newTitle = 'New',
  addNewStyle = 'standard',
  customAppendNoResultErrorMessage = { label: 'No results found', style: undefined },
  enablePagination = false,
  position = 'absolute',
  onBlur,
  onChange,
  lockedCallback,
  onFocus,
  setKeyword = () => {},
  searchCallback,
  isReadOnly = false,
  renderDropdownHeader,
  renderOption,
  renderSelectedValue,
  optionVisibility,
}: Select1Props<Data>) {
  const normalizedOptions = useMemo<SelectOption<Data>[]>(() => {
    if (optionsProp instanceof Array) {
      return optionsProp.filter((o) => {
        if (optionVisibility) return optionVisibility(o);

        return true;
      });
    }

    return Object.entries(optionsProp).map(([value, label]) => ({
      label: extractTextFromJSX(label),
      value,
    })) as SelectOption<Data>[];
  }, [optionsProp]);

  const [currentValue, setCurrentValue] = useState(input);

  const [currentLabel, setCurrentLabel] = useState<string | React.ReactNode>(
    (() => {
      if (input === 'new' && allowNew) return newTitle;

      if (labelOverride) return labelOverride;

      if (optionsProp instanceof Array) {
        const found = optionsProp.find((o) => o.value === input);

        if (!found) return '';

        if (renderSelectedValue) return renderSelectedValue(found);

        return found.label || '';
      }

      return optionsProp[input] || '';
    })(),
  );
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [blurEvent, setBlurEvent] = useState<React.FocusEvent<HTMLInputElement>>();
  const [hasKeyEvent, setHasKeyEvent] = useState(false);
  const [filter, setFilter] = useState('');
  const [startIndex, setStartIndex] = useState(-1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<SelectOption<Data>[]>([]);
  const [isFocused, setIsFocused] = useState(false);
  const [page, setPage] = useState(1);
  const [hasLoadMore, setHasLoadMore] = useState(false);
  const PAGE_SIZE = 20;

  const filteredFeatures = orderLockedFeatures().filter(({ label }) => {
    if (typeof label === 'string') {
      return label.toUpperCase().includes(disableFiltering ? '' : filter.toUpperCase()) || filter === '';
    }

    return true;
  });
  const log = useDatadogLog();

  useEffect(() => {
    if (shouldClearCurrentLabel) {
      setCurrentLabel('');
    }
  }, [shouldClearCurrentLabel]);

  useEffect(() => {
    if (disableFiltering || filter === '') {
      setFilteredOptions(normalizedOptions);
    } else {
      const filtered = normalizedOptions.filter((o) => o.label.toLowerCase().includes(filter.toLowerCase()));

      setFilteredOptions(filtered);
    }
  }, [filter, normalizedOptions]);

  useEffect(() => {
    if (!isMenuVisible) return () => {};
    if (!searchCallback) return () => {};

    // Clear the previous timer (if any) when the searchTerm changes
    const timerId = setTimeout(() => {
      searchCallback(filter);
    }, 1000);

    return () => clearTimeout(timerId);
  }, [filter]);

  useEffect(() => {
    if (disableFiltering) {
      setFilteredOptions(normalizedOptions);
    } else {
      const filtered = normalizedOptions.filter(({ label }) => label.toLowerCase().includes(searchTerm.toLowerCase()));

      setFilteredOptions(filtered);
    }
  }, [searchTerm, optionsProp]);
  const inputEl = useRef<HTMLInputElement>(null);
  const inputLabelArea = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (isMenuVisible === false) {
      setSearchTerm('');
      setKeyword('');
    } else if (isMenuVisible) {
      const valueToScroll = currentValue; // Replace this with the value you're looking for
      const container = menuRef.current;
      const elementToScroll = container?.querySelector(`[data-value="${valueToScroll}"]`) as HTMLElement;

      if (container && elementToScroll) {
        const containerRect = container.getBoundingClientRect();
        const elementRect = elementToScroll.getBoundingClientRect();

        // Calculate the element's position relative to the container
        const offsetTop = elementRect.top - containerRect.top + container.scrollTop;

        // Adjust scrollTop to position the element at the desired location
        const targetScrollTop = offsetTop - container.clientHeight / 2 + elementToScroll.offsetHeight / 2;
        container.scrollTop = targetScrollTop;
      }
    }
  }, [isMenuVisible]);

  useEffect(() => {
    if (type !== 'select' && filteredOptions.length >= 1 && searchTerm !== '') {
      setIsMenuVisible(true);
    }
  }, [filteredOptions]);

  useEffect(() => {
    const handleScroll = (event: Event) => {
      const menu = menuRef.current;
      const button = buttonRef.current;

      if (menu && button) {
        const target = event.target as HTMLElement;

        if (target !== menu && target !== button) {
          window.removeEventListener('scroll', handleScroll, true);
          setIsMenuVisible(false);
        }
      }
    };
    if (isMenuVisible && buttonRef.current && menuRef.current && position === 'fixed') {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();

        menuRef.current.style.transform = `translate(${rect.left}px, ${rect.bottom}px)`;

        menuRef.current.style.width = `${buttonRef.current.offsetWidth}px`;
        menuRef.current.style.opacity = '1';
        menuRef.current.style.visibility = 'visible';
      }
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      if (isMenuVisible && buttonRef.current && menuRef.current && position === 'fixed') {
        window.removeEventListener('scroll', handleScroll, true);
        menuRef.current.style.opacity = '0';
        menuRef.current.style.visibility = 'hidden';
      }
    };
  }, [isMenuVisible]);

  const toggleMenuVisibility = () => {
    if (disabled || isReadOnly) return;
    setIsMenuVisible(!isMenuVisible);
  };

  // TODO: this might be happening too many times
  // If the initial set value is locked select the first unlocked value
  useEffect(() => {
    if (Object.values(lockedValues || {}).some((v) => v === currentValue)) {
      setCurrentValue('');

      orderLockedFeatures().forEach((v1) => {
        if (!Object.values(lockedValues || {}).some((v) => v === v1.label)) {
          if (typeof v1.label === 'string') setCurrentValue(v1.label);
        }
      });
    }
  }, []);

  const findSelectValueByValue = (value: string) => {
    const foundOption = normalizedOptions.find((option) => option.value === value);

    if (!foundOption) return '';

    if (renderSelectedValue) return renderSelectedValue(foundOption);

    return foundOption.label;
  };

  const handleClick = (value: string) => {
    if (lockedValues && lockedCallback && value in lockedValues) {
      lockedCallback(value);

      return;
    }
    log({
      message: `${value} was selected for ${name}`,
      payload: { name, value },
      actionType: 'Option',
    });
    setIsMenuVisible(false);
    setCurrentValue(value);
    setFilter('');
    setStartIndex(-1);
    setHasKeyEvent(false);

    const customValue = findSelectValueByValue(value);
    if (showValue) {
      setCurrentLabel(value);
    } else if (customValue !== '') {
      setCurrentLabel(customValue);
    } else if (value.toLowerCase() === 'new' && allowNew) {
      setCurrentLabel(newTitle);
    } else if (labelOverride) {
      setCurrentLabel(labelOverride);
    } else {
      setCurrentLabel('');
    }
  };

  const handleDelete = () => {
    const selection = window.getSelection();
    let selectedText = '';

    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (inputLabelArea.current?.contains(range.commonAncestorContainer)) {
        selectedText = range.toString();
      }
    }
    if (currentLabel && !filter) {
      setCurrentValue('');
      setCurrentLabel('');

      setFilter('');
      setStartIndex(0);
    } else {
      const startIndexText = filter.lastIndexOf(selectedText);
      if (selectedText.length === 0) {
        setFilter(filter.substring(0, filter.length - 1));
      } else if (startIndexText !== -1) {
        const updatedFilter =
          filter.substring(0, startIndexText) + filter.substring(startIndexText + selectedText.length);
        setFilter(updatedFilter);
      } else {
        setFilter(filter.substring(0, filter.length - 1));
      }
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const clonedEvent = { ...event };
    setBlurEvent(clonedEvent); // store blur event for use on outside click
  };

  const forceFireOnChange = useCallback(() => {
    /*

    Note: this is an established hack for causing React's onChange synthetic event to fire.
    https://github.com/facebook/react/issues/11488

    Typically, input's onChange function only fires from direct user input. In this case we are using react state
    to set the value of the input, so we need to fire onChange manually.

    We are doing this so that this component can generate a React.ChangeEvent for any onChange callback that is given to it.

    */

    interface ExtendedElement extends HTMLInputElement {
      _valueTracker: { setValue: (val: string) => void };
    }

    const currentElement = inputEl.current as ExtendedElement;

    if (currentElement) {
      const lastValue = currentElement.value;

      currentElement.value = currentValue;
      currentElement._valueTracker?.setValue(lastValue);
    }

    /* eslint-enable-line @typescript-eslint/no-unsafe-call */

    inputEl.current?.dispatchEvent(new Event('change', { bubbles: true }));
  }, [currentValue]);

  useEffect(() => {
    forceFireOnChange();
  }, [currentValue]);

  useEffect(() => {
    if (currentValue !== input && !(input === '' && currentValue === 'undefined')) {
      setCurrentValue(input);
      const customValue = findSelectValueByValue(input);
      if (showValue) {
        setCurrentLabel(input);
      } else if (customValue) {
        setCurrentLabel(customValue);
      } else if (input.toLowerCase() === 'new' && allowNew) {
        setCurrentLabel(newTitle);
      } else if (labelOverride) {
        setCurrentLabel(labelOverride);
      } else {
        setCurrentLabel('');
      }
    }
  }, [input]);

  useEffect(() => {
    const customValue = findSelectValueByValue(input);
    if (showValue) {
      setCurrentLabel(input);
    } else if (customValue) {
      setCurrentLabel(customValue);
    } else if (input.toLowerCase() === 'new' && allowNew) {
      setCurrentLabel(newTitle);
    } else if (labelOverride) {
      setCurrentLabel(labelOverride);
    } else {
      setCurrentLabel('');
    }
  }, [normalizedOptions]);

  useEffect(() => {
    if (type === 'selectCompany' && input !== '') {
      setCurrentLabel(currentValue);
    }
  }, [currentValue]);

  useEffect(() => {
    if (!isMenuVisible && onFieldBlur && blurEvent) {
      const clonedEvent = { ...blurEvent };
      clonedEvent.target.id = name; // this allows blur event to have same naming convention as regular inputs
      clonedEvent.target.value = currentValue;
      onBlur(clonedEvent);
    }
  }, [isMenuVisible, blurEvent]);

  useEffect(() => {
    const hasMore = filteredFeatures?.length > page * PAGE_SIZE || filteredOptions.length > page * PAGE_SIZE;
    setHasLoadMore(hasMore);
  }, [PAGE_SIZE, page, filteredFeatures, filteredOptions]);

  const handleLoadMore = () => {
    setPage(page + 1);
  };
  const Offset = allowNew || allowEmpty ? 1 : 0;

  useEffect(() => {
    if (startIndex >= 0) {
      let list;
      if (Offset === 1 && startIndex === 0) {
        if (allowEmpty) {
          list = document.getElementById(`select-list-${name}-empty`);
        }
        if (allowNew) {
          list = document.getElementById(`select-list-${name}-new`);
        }
      } else if (
        startIndex === filteredFeatures.length + Offset ||
        (hasLoadMore && enablePagination && startIndex === page * PAGE_SIZE + Offset)
      ) {
        list = document.getElementById(`select-list-${name}-load`);
      } else {
        list = document.getElementById(`select-list-${name}-${startIndex - Offset}`);
      }
      if (list) {
        list.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [startIndex]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e);
  };

  return (
    <OutsideClickHandler
      onOutsideClick={() => {
        setIsMenuVisible(false);
        setFilter('');
        setHasKeyEvent(false);
        setStartIndex(-1);

        if (blurEvent && !onFieldBlur && isFocused) {
          const clonedEvent = { ...blurEvent };
          clonedEvent.target.id = name; // this allows blur event to have same naming convention as regular inputs
          clonedEvent.target.value = currentValue;
          onBlur(clonedEvent);
          setIsFocused(false);
        }
      }}
    >
      <div
        onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
          if (onFocus) {
            onFocus(e);
          }
          setIsFocused(true);
        }}
        className={[
          styles['select-container'],
          styles[getDisabledClass()],
          styles[getReadOnlyClass()],
          styles[`variant__${variant}`],
          renderOption ? '' : styles.defaultDropdown,
          renderSelectedValue ? '' : styles.defaultSelectedValue,
        ].join(' ')}
      >
        <input
          tabIndex={0}
          readOnly
          type="text"
          name={name}
          onBlur={handleBlur}
          onChange={handleChange}
          id={id}
          ref={inputEl}
        />

        <div
          role="textbox"
          id={`select-${name}`}
          data-testid={`select-${id || name}-trigger`}
          className={[
            styles.select,
            styles[variant],
            styles[getErrorClass()],
            styles[getDisabledClass()],
            styles[getActiveClass()],
            styles[getReadOnlyClass()],
          ].join(' ')}
          onClick={toggleMenuVisibility}
          onBlur={handleBlur}
          style={{ borderColor }}
          onKeyDown={(event) => handleKeyDown(event)}
          ref={buttonRef}
          // Removing this will break something.
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
        >
          {icon && <Box pe="sm">{icon}</Box>}

          <Box gap="sm" ai="center">
            <div
              className={styles[getLabelClass()]}
              style={
                currentLabel && labelColor !== ''
                  ? { color: labelColor }
                  : placeholder && placeholderColor
                    ? { color: placeholderColor }
                    : {}
              }
              ref={inputLabelArea}
              // eslint-disable-next-line react/no-danger
            >
              {(() => {
                const content = filter || currentLabel || placeholder;

                if (typeof content === 'string') {
                  return (
                    // TODO: replace with <span>{content}</span> and convert stringified labels throughout all of the apps
                    <span
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: content }}
                    />
                  );
                }

                if (React.isValidElement(content)) {
                  return content;
                }

                return null;
              })()}
            </div>
          </Box>
          {isLoading && <Icon icon="loader" width="30px" height="30px" />}
          {!isLoading && !disabled && !isReadOnly && <Icon icon="angleDown" size="xsm" />}
        </div>

        <ul
          id={`select-list-${name}`}
          className={[styles[getMenuVisibilityClass()], styles[position]].join(' ')}
          role="menu"
          ref={menuRef}
          style={{ maxHeight: selectionAreaHeight }}
          data-testid={`select-${id || name}-dropdown`}
        >
          {type !== 'select' && (
            <div className={styles.searchContainer}>
              <input
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setKeyword(e.target.value || '');
                }}
                placeholder="Search..."
                readOnly={isReadOnly}
              />
            </div>
          )}
          {allowEmpty && (
            <li
              id={`select-list-${name}-empty`}
              key="option-empty"
              onClick={() => handleClick('')}
              role="menuitem"
              data-value="empty"
              className={startIndex === 0 ? styles.selected : ''}
              onKeyDown={() => {}}
            >
              {' '}
            </li>
          )}
          {allowNew && (
            <li
              id={`select-list-${name}-new`}
              key="option-new"
              onClick={() => handleClick('new')}
              role="menuitem"
              data-value="new"
              className={[styles[addNewStyle], startIndex === 0 ? styles.selected : ''].join(' ')}
              onKeyDown={() => {}}
            >
              {addNewStyle === 'bolded' && <Icon icon="add" height="20px" width="20px" />} {'  '} {newTitle}
            </li>
          )}

          {renderDropdownHeader && (
            <li className={styles.renderDropdownHeader}>
              {renderDropdownHeader({ close: () => setIsMenuVisible(false) })}
            </li>
          )}

          {getOrderedOptions().length !== 0 &&
            filteredFeatures.length === 0 && ( // Check if filter returned zero results
              <>
                {customAppendNoResultErrorMessage && (
                  <li
                    id={`select-list-${name}-no-results`}
                    key="option-no-results"
                    role="menuitem"
                    data-value="no results"
                    style={customAppendNoResultErrorMessage.style}
                  >
                    {filter} {customAppendNoResultErrorMessage.label}
                  </li>
                )}
              </>
            )}

          {!enablePagination &&
            filteredFeatures.map((o, idx) => {
              const { label, value } = o;

              if (renderOption) {
                return (
                  <li
                    id={`select-list-${name}-${idx}`}
                    key={`option-${idx}`}
                    onClick={() => handleClick(value)}
                    role="menuitem"
                    data-value={value}
                    onKeyDown={() => {}}
                    className={[styles.option, styles.customOption].join(' ')}
                  >
                    {renderOption(o, { focused: optionIsFocused(idx) })}
                  </li>
                );
              }

              return (
                <li
                  id={`select-list-${name}-${idx}`}
                  key={`option-${idx}`}
                  className={[styles.option, styles[getSelectedClass(idx)], styles[getLocked(value)]].join(' ')}
                  onClick={() => handleClick(value)}
                  role="menuitem"
                  data-value={value}
                  onKeyDown={() => {}}
                >
                  {lockedValues && value in lockedValues && (
                    <div style={{ paddingRight: '10px' }}>
                      <Icon width="min-content" height="min-content" icon="lock2" size="sm" bgColor="primary-light" />
                    </div>
                  )}

                  {hasTwoValues(label) ? (
                    <div className={styles.valuesContainer}>
                      <span>
                        <b>{label.split('|')[0]}</b>
                      </span>
                      <span>{label.split('|')[1]}</span>
                    </div>
                  ) : (
                    // TODO: replace with <span>{content}</span> and convert stringified labels throughout all of the apps
                    // eslint-disable-next-line react/no-danger
                    <div dangerouslySetInnerHTML={{ __html: label }} />
                  )}
                </li>
              );
            })}

          {enablePagination &&
            filteredFeatures.slice(0, page * PAGE_SIZE).map((o, idx) => {
              const { value, label } = o;

              if (renderOption) {
                return (
                  <li
                    id={`select-list-${name}-${idx}`}
                    key={`option-${idx}`}
                    onClick={() => handleClick(value)}
                    role="menuitem"
                    data-value={value}
                    onKeyDown={() => {}}
                    className={[styles.option, styles.customOption].join(' ')}
                  >
                    {renderOption(o, { focused: optionIsFocused(idx) })}
                  </li>
                );
              }

              return (
                <li
                  id={`select-list-${name}-${idx}`}
                  key={`option-${idx}`}
                  className={[styles.option, styles[getSelectedClass(idx)], styles[getLocked(value)]].join(' ')}
                  onClick={() => handleClick(value)}
                  role="menuitem"
                  data-value={value}
                  onKeyDown={() => {}}
                >
                  {lockedValues && value in lockedValues && (
                    <div style={{ paddingRight: '10px' }}>
                      <Icon width="min-content" height="min-content" icon="lock2" size="sm" bgColor="primary-light" />
                    </div>
                  )}

                  {hasTwoValues(label) ? (
                    <div className={styles.valuesContainer}>
                      <span>
                        <b>{label.split('|')[0]}</b>
                      </span>
                      <span>{label.split('|')[1]}</span>
                    </div>
                  ) : (
                    // TODO: replace with <span>{content}</span> and convert stringified labels throughout all of the apps
                    // eslint-disable-next-line react/no-danger
                    <div dangerouslySetInnerHTML={{ __html: label }} />
                  )}
                </li>
              );
            })}

          {hasLoadMore && enablePagination && (
            <li
              id={`select-list-${name}-load`}
              key="option-new"
              onClick={handleLoadMore}
              role="menuitem"
              className={[
                styles.bolded,
                filteredFeatures.length + Offset === startIndex || startIndex === page * PAGE_SIZE + Offset
                  ? styles.selected
                  : '',
              ].join(' ')}
              onKeyDown={() => {}}
            >
              <Icon icon="add" height="20px" width="20px" /> Load More
            </li>
          )}
        </ul>

        <ErrorText error={error} size="sm" indentation fitContent />
      </div>
    </OutsideClickHandler>
  );

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (type === 'selectCompany') return;
    if (!isMenuVisible) toggleMenuVisibility();
    const handlers: { [key: string]: () => void } = {
      Backspace() {
        handleDelete();
      },
      Delete: () => {
        handleDelete();
      },
      Space: () => {
        setFilter(`${filter}${event.key}`);
        event.preventDefault();
      },
      ArrowUp: () => {
        if (startIndex > 0) {
          setStartIndex(Math.max(startIndex - 1, 0));
        }
        event.preventDefault();
      },
      ArrowDown: () => {
        if (enablePagination) {
          setStartIndex(Math.min(startIndex + 1, page * PAGE_SIZE + Offset, filteredFeatures.length + Offset - 1));
        } else {
          setStartIndex(Math.min(startIndex + 1, filteredFeatures.length + Offset - 1));
        }

        event.preventDefault();
      },
      Enter: () => {
        if (isMenuVisible) {
          if (startIndex === 0 && Offset === 1) {
            if (allowEmpty) {
              handleClick('');
            }
            if (allowNew) {
              handleClick('new');
            }
          } else if (
            startIndex === filteredFeatures.length + Offset ||
            (hasLoadMore && enablePagination && startIndex === page * PAGE_SIZE + Offset)
          ) {
            handleLoadMore();
          } else {
            if (startIndex - Offset >= 0) {
              handleClick(filteredFeatures[startIndex - Offset].value);
            }
          }
        }
      },
      default: () => {},
    };

    const handler = event.key.length === 1 ? handlers.Space : handlers[event.key] || handlers.default;

    setHasKeyEvent(true);
    handler();
  }

  function getOrderedOptions() {
    const list = type === 'select' ? normalizedOptions : filteredOptions;

    if (noOrdering) {
      return list;
    }

    if (orderByValues) {
      const isNumericList = list.every((opt) => /^\d+$/.test(opt.value));

      if (isNumericList) {
        list.sort((a, b) => parseInt(a.value, 10) - parseInt(b.value, 10));
      } else {
        list.sort((a, b) => a.label.localeCompare(b.label));
      }

      return reverse ? list.reverse() : list;
    }

    list.sort();

    return reverse ? list.reverse() : list;
  }

  function orderLockedFeatures() {
    const list = getOrderedOptions();

    if (!lockedValues) {
      return list;
    }

    const orderedList = list
      .filter(({ value }) => !(value in lockedValues))
      .concat(list.filter(({ value }) => value in lockedValues));

    return orderedList;
  }

  function getMenuVisibilityClass() {
    return isMenuVisible ? 'visible' : 'hidden';
  }

  function getLabelClass() {
    return currentLabel ? 'label' : 'placeholder';
  }

  function getErrorClass() {
    return error ? 'hasError' : '';
  }

  function getDisabledClass() {
    return disabled ? 'disabled' : '';
  }

  function getReadOnlyClass() {
    return isReadOnly ? 'readOnly' : '';
  }

  function optionIsFocused(idx: number) {
    return idx + Offset === startIndex && hasKeyEvent;
  }

  function getSelectedClass(idx: number) {
    return optionIsFocused(idx) ? 'selected' : '';
  }

  function getLocked(value: string) {
    return lockedValues && value in lockedValues ? 'locked-select-item' : '';
  }

  function getActiveClass() {
    return isActive ? 'active' : '';
  }

  function hasTwoValues(label: unknown): label is string {
    return hasTwoValueProp && typeof label === 'string';
  }
}

export default Select1;
